import { Injectable } from '@angular/core';
import { ApiClient } from '../api.client';

import { BookingItem } from '../../models/booking-item';
import { BookingSearchResultModel } from '../../bookings/shared/booking-search-result.model';
import { Item, Product } from '../../models';
import { ProductCategory } from '../../models/product-category';
import { AvailabilityResponse } from '../../bookings/shared/availability-response.model';
import { map } from 'rxjs/operators';

@Injectable()
export class BookingItemService {
  apiUrl: string;

  constructor(private http: ApiClient) {
    this.apiUrl = this.http.API_URL;
  }

  create(bookingId: number, attributes) {
    return this.http.post<BookingItem>(`booking/${bookingId}/item`, attributes);
  }

  getAll(bookingId: number) {
    return this.http.get<BookingItem[]>(`booking/${bookingId}/item`);
  }

  get(id: number, bookingId: number) {
    return this.http.get(`booking/${bookingId}/item/${id}`);
  }

  update(id: number, bookingId: number, booking: Partial<BookingItem>) {
    return this.http.put(`booking/${bookingId}/item/${id}`, booking);
  }

  delete(id: number, bookingId: number) {
    return this.http.delete(`booking/${bookingId}/item/${id}`);
  }

  bulkUpdate(items: any[], bookingId) {
    return this.http.put(`booking/${bookingId}/item/bulkUpdate`, {
      items: items
    });
  }

  productsToPossibleBookingItems(results: BookingSearchResultModel) {
    let bookingItems: BookingItem[] = [];
    bookingItems = bookingItems.concat(
      results.items.map((item) => mapItemToBookingItem(item))
    );
    bookingItems = bookingItems.concat(
      results.products.map((product) => mapProductToBookingItem(product))
    );
    bookingItems = bookingItems.concat(
      results.categories.map((category) => mapCategoryToBookingItem(category))
    );
    return bookingItems;
  }

  getProspectiveAvailability(payload: {
    on_hire: string;
    off_hire: string;
    product_id?: number;
    category_id?: number;
    item_id?: number;
  }) {
    return this.http.post<AvailabilityResponse[]>(`item-availability`, {
      item_id: null,
      product_id: null,
      category_id: null,
      ...payload
    });
  }

  getAvailability(bookingId: number, booking_item_id: number) {
    return this.http.get<AvailabilityResponse[]>(
      `booking/${bookingId}/item/${booking_item_id}/availability`
    );
  }
}

export function mapItemToBookingItem(item: Item): BookingItem {
  const uncategorised = <any>{ name: 'Uncategorised', id: -1 };
  return {
    booking_id: null,
    product_id: item.product_id,
    category_id: item.product.product_category_id ?? -1,
    item_id: item.id,
    quantity: 1,
    notes: null,
    charge: null,
    item: item,
    product: item.product,
    category: item.product.category ? item.product.category : uncategorised
  };
}

export function mapCategoryToBookingItem(
  category: ProductCategory
): BookingItem {
  return {
    booking_id: null,
    category_id: category.id,
    item_id: null,
    quantity: 1,
    notes: null,
    charge: null,
    category: category,
    product: null,
    item: null
  };
}

export function mapProductToBookingItem(product: Product): BookingItem {
  const uncategorised = <any>{ name: 'Uncategorised', id: -1 };
  return {
    booking_id: null,
    item_id: null,
    product_id: product.id,
    category_id: product.product_category_id ?? -1,
    quantity: 1,
    notes: null,
    charge: null,
    product: product,
    category: product.category ?? uncategorised,
    item: null
  };
}
